import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import { showDialogOverlay, hideDialogOverlay } from "../../../utils/dialogOverlay.js";
import { apiBaseUrl, fetchJson, parseJsonResponse } from "./create/api.js";
import { renderDemirbasGrid } from "../demirbasGrid.js";
import { uiState } from "../../../state/uiState.js";
import IMask from "imask";
import AutoNumeric from "autonumeric";
import * as FilePond from "filepond";
import "filepond/dist/filepond.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import { initCreateDialogDropdowns } from "./create/createDropdowns.js";
import { fillSelect } from "./create/fillSelect.js";
import { truncateLabel } from "../../../utils/textPresentation.js";
import { getTodayLocalDateInputValue, toDateInputValue } from "../../../utils/dateOnly.js";
import {
	FATURA_CURRENCY_OPTIONS,
	buildFaturaKdvOptions,
	calculateFaturaTotals,
	formatFaturaAmount,
	getFaturaCurrencySymbol,
	normalizeFaturaQuantityInputValue,
	normalizeFaturaCurrency,
	parseFaturaQuantity,
	roundFaturaAmount,
} from "./faturaCurrency.js";
import { bindAmountPasteGuard } from "../amountPasteGuard.js";
import {
	populateAmortismanSelect,
	readAmortismanSelection,
	updateAmortismanPreview,
} from "../../amortisman/amortismanUtils.js";

FilePond.registerPlugin(
    FilePondPluginImagePreview,
    FilePondPluginImageExifOrientation,
    FilePondPluginFileValidateSize,
    FilePondPluginFileValidateType,
);

// ─── Debug logging ─────────────────────────────────────────────────────────────
const DEBUG = true; // geliştirme bitti → false yap
const dbg = (...args) => { if (DEBUG) console.log("[editDialog]", ...args); };

// ─── Modül-level state ────────────────────────────────────────────────────────
// create/faturaSection.js'deki global faturaInstances'dan ayrı
const editFaturaInstances = new Map(); // key: string faturaId → { root, pond, anFiyat, kdvEl, faturaDbId, hasFile }
let editCurrentId = null;
let didBindEditDialog = false;
let editDialogExpiryTimer = null;
let editDialogDraftDeadline = null;
let editDialogDraftTicker = null;
let editDialogLoadVersion = 0;
let editAmortismanDefinitions = [];

// ─── Toast ────────────────────────────────────────────────────────────────────
function showToast(message, type = "success", inDialog = true) {
    Toastify({
        text: message,
        duration: 3000,
        gravity: "top",
        position: "right",
        stopOnFocus: true,
        selector: inDialog ? document.querySelector("#editDialog") : document.body,
        style: {
            background: type === "success" ? "#22c55e" : "#ee4447",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500",
            zIndex: "99999",
            position: "fixed",
        },
    }).showToast();
}

// ─── Fatura yardımcıları ──────────────────────────────────────────────────────
const tl = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function clearEditDialogExpiryTimer() {
    if (editDialogExpiryTimer) {
        clearTimeout(editDialogExpiryTimer);
        editDialogExpiryTimer = null;
    }
}

function clearEditDraftTicker() {
    if (editDialogDraftTicker) {
        clearInterval(editDialogDraftTicker);
        editDialogDraftTicker = null;
    }
}

function isEditDialogLoadStale(editDialog, loadVersion, demirbasId) {
    return !editDialog?.open || loadVersion !== editDialogLoadVersion || editCurrentId !== demirbasId;
}

function canMountEditDialogNode(node, editDialog = null, loadVersion = null, demirbasId = null) {
    if (!node?.isConnected) return false;
    if (!editDialog) return true;
    return !isEditDialogLoadStale(editDialog, loadVersion, demirbasId);
}

function findEditFaturaField(cardEl, fieldName) {
    return cardEl?.querySelector(`[data-edit-fatura-field="${fieldName}"]`) ?? null;
}

function hasEditDraftUploads(editDialog) {
    const imagePath = editDialog.querySelector("#edit_urun_gorsel_path")?.value || "";
    if (isDraftUploadPath(imagePath)) return true;
    return [...editDialog.querySelectorAll(".fatura-pdf-path")].some((input) =>
        isDraftUploadPath(input.value || ""),
    );
}

function renderEditDraftTimer(editDialog) {
    const pill = editDialog.querySelector(".draft-timer-pill");
    const textEl = editDialog.querySelector(".draft-timer-text");
    if (!pill || !textEl || !editDialogDraftDeadline) return;

    const remainingMs = Math.max(0, editDialogDraftDeadline - Date.now());
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const minutes = String(Math.floor(remainingSeconds / 60)).padStart(2, "0");
    const seconds = String(remainingSeconds % 60).padStart(2, "0");

    pill.hidden = false;
    pill.classList.toggle("is-warning", remainingSeconds <= 5 * 60);
    textEl.textContent = `${minutes}:${seconds}`;
}

function resetEditDraftTimerUi(editDialog) {
    const pill = editDialog.querySelector(".draft-timer-pill");
    if (pill) {
        pill.hidden = true;
        pill.classList.remove("is-warning");
    }
    editDialogDraftDeadline = null;
    clearEditDraftTicker();
}

function ensureEditDraftTimer(editDialog) {
    if (!editDialog.open) return;
    if (!hasEditDraftUploads(editDialog)) {
        clearEditDialogExpiryTimer();
        resetEditDraftTimerUi(editDialog);
        return;
    }

    if (!editDialogDraftDeadline) {
        editDialogDraftDeadline = Date.now() + 15 * 60 * 1000;
        clearEditDialogExpiryTimer();
        editDialogExpiryTimer = setTimeout(() => {
            if (!editDialog.open) return;
            showToast("Taslak suresi doldu.", "error", false);
            editDialog.close();
        }, 15 * 60 * 1000);
        clearEditDraftTicker();
        editDialogDraftTicker = setInterval(() => {
            if (!editDialog.open || !editDialogDraftDeadline) return;
            renderEditDraftTimer(editDialog);
        }, 1000);
    }

    renderEditDraftTimer(editDialog);
}

function isDraftUploadPath(filePath) {
    return typeof filePath === "string" && filePath.startsWith("/uploads/_drafts/");
}

function buildCurrencyOptions(selected = "TRY") {
    const normalized = normalizeFaturaCurrency(selected);
    return FATURA_CURRENCY_OPTIONS.map(
        (option) => `<option value="${option.code}"${option.code === normalized ? " selected" : ""}>${option.symbol}</option>`,
    ).join("");
}

function revertDraftFile(serverId) {
    if (!isDraftUploadPath(serverId)) return Promise.resolve();
    return fetch(`${apiBaseUrl}/upload/revert`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serverId }),
    }).catch((err) => {
        console.error("edit draft revert hatasi:", err);
    });
}

function setEditUploadState(cardEl, state) {
    const fieldEl = cardEl.querySelector(".column-container.create-gorsel.create-fatura");
    if (!fieldEl) return;
    fieldEl.classList.remove("is-valid", "is-invalid");
    if (state) fieldEl.classList.add(state);
}

function updateEditInfoCard(faturaId) {
    const inst = editFaturaInstances.get(faturaId);
    if (!inst) return;
    const { root, anFiyat, quantityEl, kdvEl, currencyEl } = inst;
    const kdvTutarOut = root.querySelector(".fatura-kdv-deger");
    const toplamOut = root.querySelector(".fatura-toplam-deger");
    if (!kdvTutarOut || !toplamOut) return;

    const raw = anFiyat?.getNumericString?.();
    const birimFiyat = raw !== null && raw !== undefined && raw !== "" ? Number(raw) : null;
    const rawBirimMiktar = String(quantityEl?.value ?? "").trim();
    const kdvOran = kdvEl?.value ? Number(kdvEl.value) : null;
    const currencyCode = normalizeFaturaCurrency(currencyEl?.value);
    const totals = calculateFaturaTotals({
        birimFiyat,
        birimMiktar: quantityEl?.value,
        kdvOrani: kdvOran,
    });

    if (birimFiyat === null || (rawBirimMiktar !== "" && parseFaturaQuantity(rawBirimMiktar) === null)) {
        kdvTutarOut.textContent = "—";
        toplamOut.textContent = "—";
        kdvTutarOut.title = "—";
        toplamOut.title = "—";
        return;
    }

    const kdvText = formatFaturaAmount(tl, totals.kdvTutar, currencyCode);
    const toplamText = formatFaturaAmount(tl, totals.satinAlmaDegeri, currencyCode);

    kdvTutarOut.textContent = kdvText;
    toplamOut.textContent = toplamText;
    kdvTutarOut.title = kdvText;
    toplamOut.title = toplamText;
}

function bindEditQuantityValidation(quantityInput, onChange) {
    if (!quantityInput || quantityInput.dataset.boundQuantity === "true") return;
    quantityInput.dataset.boundQuantity = "true";

    const errorMessage = "Temsil adedi en az 1 olan tam sayi olmalidir.";
    const emitChange = () => {
        if (typeof onChange === "function") onChange();
    };

    quantityInput.addEventListener("input", () => {
        quantityInput.setCustomValidity("");
        emitChange();
    });

    quantityInput.addEventListener("blur", () => {
        const rawValue = quantityInput.value.trim();
        if (!rawValue) {
            quantityInput.value = normalizeFaturaQuantityInputValue("", 1);
            quantityInput.setCustomValidity("");
            emitChange();
            return;
        }

        const parsed = parseFaturaQuantity(rawValue);
        if (parsed === null) {
            quantityInput.setCustomValidity(errorMessage);
            quantityInput.reportValidity();
            emitChange();
            return;
        }

        quantityInput.value = normalizeFaturaQuantityInputValue(parsed, 1);
        quantityInput.setCustomValidity("");
        emitChange();
    });
}

function renumberEditFaturas() {
    const list = document.querySelector("#editFaturaList");
    if (!list) return;
    [...list.querySelectorAll(".fatura-container")].forEach((card, idx) => {
        const el = card.querySelector(".fatura-index");
        if (el) el.textContent = `#${idx + 1}`;
    });
}


function ensureFaturaDeleteDialog() {
    if (document.getElementById("editFaturaDeleteDialog")) return;
    document.body.insertAdjacentHTML("beforeend", `
<dialog id="editFaturaDeleteDialog">
    <div class="delete-dialog-container">
        <div class="delete-dialog-header">
            <div class="delete-header-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
            </div>
            <h2>Yeni Faturayı Kaldır</h2>
            <button type="button" class="delete-dialog-close efdd-close">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>
        </div>
        <hr class="delete-divider"/>
        <div class="delete-dialog-body">
            <p>Henüz kaydedilmemiş bu faturayı listeden kaldırmak istediğinizden emin misiniz?</p>
            <div class="delete-dialog-card">
                <div class="delete-card-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                    </svg>
                </div>
                <div class="delete-card-info">
                    <span class="delete-dialog-id efdd-index"></span>
                    <span class="delete-dialog-name efdd-fatura-no"></span>
                </div>
            </div>
            <div class="delete-dialog-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>Bu işlem yalnızca düzenleme sırasında eklediğiniz yeni kartı kaldırır. Kaydedilmiş alım partilerine dokunulmaz.</span>
            </div>
        </div>
        <div class="delete-dialog-buttons">
            <button type="button" class="delete-cancel efdd-cancel">İptal</button>
            <button type="button" class="delete-confirm efdd-confirm">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
                Kaldır
            </button>
        </div>
    </div>
</dialog>`);
}

function openFaturaDeleteConfirm(card, faturaList) {
    const dialog = document.getElementById("editFaturaDeleteDialog");
    if (!dialog) return;
	const instance = editFaturaInstances.get(card?.dataset?.faturaId);
	if (instance?.faturaDbId != null) {
		showToast("Kaydedilmis alim partileri silinemez; takip ve alim partisi arsiv akisini kullanin.", "error");
		return;
	}
    const all = [...faturaList.querySelectorAll(".fatura-container")];
    const idx = all.indexOf(card);
    const faturaNo = findEditFaturaField(card, "fatura_no")?.value?.trim() || null;
    dialog.querySelector(".efdd-index").textContent = `Fatura #${idx + 1}`;
    dialog.querySelector(".efdd-fatura-no").textContent = faturaNo || "Fatura numarası girilmemiş";
    dialog._pendingCard = card;
    dialog._pendingList = faturaList;
    dialog.showModal();
}

// ─── Dialog HTML ──────────────────────────────────────────────────────────────
function ensureEditDialog() {
    if (document.getElementById("editDialog")) return;
    document.body.insertAdjacentHTML("beforeend", `
<dialog id="editDialog" class="create-dialog">
  <div class="edit-loading-overlay" hidden>
    <div class="edit-loading-spinner"></div>
    <span class="edit-loading-text">Yükleniyor...</span>
  </div>
  <form method="dialog" novalidate class="create-dialog-container">
    <div class="create-dialog-header">
      <h4>Demirbaş Düzenle</h4>
    </div>
    <div class="draft-timer-pill" hidden>
      <span class="material-icons">schedule</span>
      <span class="draft-timer-text">15:00</span>
    </div>
    <div class="create-dialog-content">

      <div class="temel-bilgiler section">
        <div class="section-1-header"><h5>Temel Bilgiler</h5></div>
        <div class="section-1-content">
          <div class="column-container field">
            <label for="edit_departman">Departman:</label>
            <select id="edit_departman" class="form-select" name="edit_departman" required>
              <option value="" selected>Departman seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Departman seçiniz." data-invalid="Geçerli bir departman seçmelisiniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_urun_grubu">Ürün Grubu:</label>
            <select id="edit_urun_grubu" class="form-select" name="edit_urun_grubu" required>
              <option value="" selected>Ürün grubu seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Ürün grubu seçiniz." data-invalid="Geçerli bir ürün grubu seçmelisiniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_urun_alt_grubu">Ürün Alt Grubu:</label>
            <select id="edit_urun_alt_grubu" class="form-select" name="edit_urun_alt_grubu" required>
              <option value="" selected>Ürün alt grubu seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Ürün alt grubu seçiniz." data-invalid="Geçerli bir ürün alt grubu seçmelisiniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_urun_cinsi">Ürün Cinsi:</label>
            <select id="edit_urun_cinsi" class="form-select" name="edit_urun_cinsi" required>
              <option value="" selected>Ürün cinsi seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Ürün cinsi seçiniz." data-invalid="Geçerli bir ürün cinsi seçmelisiniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_marka">Marka:</label>
            <select id="edit_marka" class="form-select" name="edit_marka" required>
              <option value="" selected>Marka seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Marka seçiniz." data-invalid="Geçerli bir marka seçmelisiniz."></span>
          </div>
          <div class="column-container field create-model">
            <label for="edit_model">Model:</label>
            <select id="edit_model" class="form-select" name="edit_model" required>
              <option value="" selected>Model seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Model seçiniz." data-invalid="Geçerli bir model seçmelisiniz."></span>
          </div>
          <div class="column-container field seri-numarasi">
            <label for="edit_seri_numarasi">Seri Numarası:</label>
            <input id="edit_seri_numarasi" class="form-input" type="text" placeholder="Seri numarası" maxlength="150"/>
            <span class="info" data-focus="(4-150 karakter, A-Z, 0-9, -)" data-invalid="Seri numarası 4-150 karakter olmalı. Sadece A-Z, 0-9 ve '-' kullanın."></span>
          </div>
          <div class="column-container field create-aciklama">
            <label for="edit_aciklama">Açıklama:</label>
            <textarea id="edit_aciklama" class="form-textarea" placeholder="Açıklama yazabilirsiniz" maxlength="500"></textarea>
            <span class="info" data-focus="Maksimum 500 karakter." data-invalid="Açıklama 500 karakteri geçemez."></span>
          </div>
        </div>
      </div>

      <div class="fatura-bilgiler section">
        <div class="section-1-header"><h5>Fatura Bilgileri</h5></div>
        <div class="section-1-content" id="editFaturaList"></div>
        <div class="add-new-fatura">
          <button class="btn-add" type="button">Yeni Fatura Ekle</button>
        </div>
      </div>

      <div class="zimmet-lokasyon-bilgiler section">
        <div class="section-1-header"><h5>Zimmet &amp; Lokasyon</h5></div>
        <div class="section-1-content">
          <div class="column-container field create-urun-durumu">
            <label for="edit_urun_durumu">Ürün Durumu:</label>
            <select id="edit_urun_durumu" class="form-select" name="edit_urun_durumu" required>
              <option value="" selected>Ürün durumu seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Ürün durumu seçiniz." data-invalid="Geçerli bir ürün durumu seçmelisiniz."></span>
          </div>
          <div class="column-container field create-zimmet-durumu">
            <label for="edit_zimmet_durumu">Zimmet Durumu:</label>
            <select id="edit_zimmet_durumu" class="form-select" name="edit_zimmet_durumu" required>
              <option value="" selected>Zimmet durumu seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Zimmet durumu seçiniz." data-invalid="Geçerli bir zimmet durumu seçmelisiniz."></span>
          </div>
          <div class="column-container field create-zimmetlenen-kisi">
            <label for="edit_zimmetlenen_kisi">Zimmetlenen Kişi:</label>
            <select id="edit_zimmetlenen_kisi" class="form-select" name="edit_zimmetlenen_kisi" required>
              <option value="" selected>Zimmetlenen kişi seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Zimmetlenen kişi seçiniz." data-invalid="Geçerli bir zimmetlenen kişi seçmelisiniz."></span>
          </div>
          <div class="info-card-yeni" id="editZimmetInfoCard">
            <div class="info-card-yeni-satir">
              <div class="info-card-yeni-etiket">T.C. Kimlik No</div>
              <div class="info-card-yeni-deger info-card-yeni-deger--mono text-1 zimmet-tc-deger">—</div>
            </div>
            <div class="info-card-yeni-satir">
              <div class="info-card-yeni-etiket">Ünvan</div>
              <div class="info-card-yeni-deger text-2 zimmet-unvan-deger">—</div>
            </div>
            <div class="info-card-yeni-satir">
              <div class="info-card-yeni-etiket">Departman</div>
              <div class="info-card-yeni-deger text-3 zimmet-departman-deger">—</div>
            </div>
          </div>
          <div class="column-container field create-zimmet-tarihi">
            <label for="edit_zimmet_tarihi">Zimmet Tarihi:</label>
            <input id="edit_zimmet_tarihi" class="form-input" type="date" required/>
            <span class="info" data-invalid="Geçerli bir zimmet tarihi giriniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_lokasyon">Lokasyon:</label>
            <select id="edit_lokasyon" class="form-select" name="edit_lokasyon" required>
              <option value="" selected>Lokasyon seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Lokasyon seçiniz." data-invalid="Geçerli bir lokasyon seçmelisiniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_oda">Oda:</label>
            <select id="edit_oda" class="form-select" name="edit_oda" required>
              <option value="" selected>Oda seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Oda seçiniz." data-invalid="Geçerli bir oda seçmelisiniz."></span>
          </div>
          <div class="column-container field">
            <label for="edit_yer">Yer:</label>
            <select id="edit_yer" class="form-select" name="edit_yer" required>
              <option value="" selected>Yer seçiniz</option>
            </select>
            <span class="info" data-required="Bu alan zorunludur." data-focus="Yer seçiniz." data-invalid="Geçerli bir yer seçmelisiniz."></span>
          </div>
        </div>
      </div>

      <div class="urun-gorsel section">
        <div class="section-1-header"><h5>Ürün Görsel</h5></div>
        <div class="section-1-content">
          <div class="column-container create-gorsel">
            <label>Ürün Resmi</label>
            <div class="gorsel-container">
              <input type="file" class="filepond edit-gorsel"/>
              <input type="hidden" id="edit_urun_gorsel_path" value=""/>
            </div>
            <div class="draft-upload-note">
              <span class="material-icons">info</span>
              <span>Yuklenen dosyalar once taslak olarak tutulur. Kaydetmezseniz 15 dakika sonra otomatik silinir.</span>
            </div>
          </div>
        </div>
      </div>

    </div>
    <div class="create-dialog-buttons">
      <button type="button" class="edit-cancel">İptal</button>
      <button type="submit" class="create-save">Kaydet</button>
    </div>
  </form>
</dialog>`);
}

// ─── initEditDialog — bir kere çağrılır ──────────────────────────────────────
export function initEditDialog() {
    ensureEditDialog();
    ensureFaturaDeleteDialog();
    const editDialog = document.getElementById("editDialog");
    if (!editDialog || didBindEditDialog) return;
    didBindEditDialog = true;
    editDialog.addEventListener("draft-upload-change", () => ensureEditDraftTimer(editDialog));

    // Fatura silme confirm dialog — butonları bir kez bağla
    const faturaConfirmDialog = document.getElementById("editFaturaDeleteDialog");
    if (faturaConfirmDialog) {
        faturaConfirmDialog.addEventListener("click", (e) => {
            if (e.target.closest(".efdd-close") || e.target.closest(".efdd-cancel")) {
                faturaConfirmDialog._pendingCard = null;
                faturaConfirmDialog._pendingList = null;
                faturaConfirmDialog.close();
            }
            if (e.target.closest(".efdd-confirm")) {
                const card = faturaConfirmDialog._pendingCard;
                const list = faturaConfirmDialog._pendingList;
                faturaConfirmDialog._pendingCard = null;
                faturaConfirmDialog._pendingList = null;
                faturaConfirmDialog.close();
                if (card && list) {
                    destroyOneEditFaturaCard(card);
                    card.remove();
                    renumberEditFaturas();
                }
            }
        });
    }

    // İptal / kapat
    document.body.addEventListener("click", (e) => {
        if (e.target.closest(".edit-cancel")) editDialog.close();
    });

    // Dialog kapanınca temizle
    editDialog.addEventListener("close", () => {
        dbg("close event — cleaning up");
        editDialogLoadVersion += 1;
        clearEditDialogExpiryTimer();
        resetEditFaturaSection();
        editCurrentId = null;
        editDialog.removeAttribute("data-submitted");

        // Görsel FilePond'u sıfırla
        resetEditGorselPond(editDialog);
    });

    // Yeni fatura ekle butonu
    const addBtn = editDialog.querySelector(".add-new-fatura .btn-add");
    if (addBtn && !addBtn.dataset.bound) {
        addBtn.dataset.bound = "true";
        addBtn.addEventListener("click", () => {
            const list = document.querySelector("#editFaturaList");
            if (!list) return;
            const ids = [...list.querySelectorAll(".fatura-container")]
                .map(c => Number(c.dataset.faturaId || 0))
                .filter(n => Number.isFinite(n));
            const nextId = (ids.length ? Math.max(...ids) : 0) + 1;
            const card = document.createElement("div");
            card.className = "fatura-container";
            card.dataset.faturaId = String(nextId);
            card.innerHTML = buildEditFaturaHtml();
            list.appendChild(card);
            initOneEditFaturaCard(card);
            renumberEditFaturas();
        });
    }

    // Fatura silme (delegation)
    const faturaList = editDialog.querySelector("#editFaturaList");
    if (faturaList && !faturaList.dataset.bound) {
        faturaList.dataset.bound = "true";
        faturaList.addEventListener("click", (e) => {
            const delBtn = e.target.closest(".delete-button");
            if (!delBtn) return;
            const card = delBtn.closest(".fatura-container");
            if (!card) return;
            const all = [...faturaList.querySelectorAll(".fatura-container")];
            if (all.length <= 1) {
                showToast("En az 1 fatura olmalıdır. Son faturayı kaldıramazsınız.", "error");
                return;
            }
            openFaturaDeleteConfirm(card, faturaList);
        });
    }

    // Submit
    const form = editDialog.querySelector("form.create-dialog-container");
    if (form && !form._payloadBound) {
        form._payloadBound = true;
        form.addEventListener("submit", async (e) => {
            e.preventDefault();
            editDialog.dataset.submitted = "true";

            if (!form.checkValidity()) {
                showToast("Lütfen tüm zorunlu alanları doldurunuz.", "error");
                form.querySelector(":invalid")?.scrollIntoView({ behavior: "smooth", block: "center" });
                return;
            }

            // Fatura PDF kontrolü — her kartın dosyası olmalı
            const faturaInstList = [...editFaturaInstances.values()];
            const faturaHazirDegil = faturaInstList.some(inst => {
                if (!inst.pond) return true;
                if (!inst.hasFile) return true;
                return !inst.currentFilePath;
            });

            if (faturaInstList.length === 0) {
                showToast("En az 1 fatura olmalıdır.", "error");
                return;
            }

            if (faturaHazirDegil) {
                showToast("Her fatura için PDF yüklenmelidir.", "error");
                return;
            }

            // Görsel yüklenme kontrolü
            const gorselInput = editDialog.querySelector(".edit-gorsel");
            const gorselPath  = editDialog.querySelector("#edit_urun_gorsel_path");
            const gorselFileAdded = gorselInput?._editPond?.getFiles()?.length > 0;
            const gorselHazirDegil = gorselFileAdded && !gorselPath?.value;
            if (gorselHazirDegil) {
                showToast("Görsel yüklenmesi tamamlanmadı, lütfen bekleyiniz.", "error");
                return;
            }

            // Payload
            const v = (sel) => editDialog.querySelector(sel)?.value ?? "";
            const emptyToNull = (s) => { const t = String(s ?? "").trim(); return t === "" ? null : t; };

            // Fatura kartlarını ayır: yeni (faturaDbId=null) vs mevcut (faturaDbId!=null)
            const yeniFaturalar = [];
            const guncelFaturalar = [];
            editFaturaInstances.forEach((inst) => {
                const card = inst.root;
                const raw = inst.anFiyat?.getNumericString?.();
                const birimFiyat = raw !== null && raw !== undefined && raw !== "" ? Number(raw) : null;
                const rawBirimMiktar = String(findEditFaturaField(card, "birim_miktar")?.value ?? "").trim();
                const birimMiktar = rawBirimMiktar === "" ? 1 : parseFaturaQuantity(rawBirimMiktar);
                const kdvOran    = inst.kdvEl?.value ? Number(inst.kdvEl.value) : null;
                const totals = calculateFaturaTotals({
                    birimFiyat,
                    birimMiktar,
                    kdvOrani: kdvOran,
                });
				const amortismanSelection = readAmortismanSelection(
					findEditFaturaField(card, "amortisman"),
				);
                const faturaDatum = {
                    fatura_numarasi:   emptyToNull(findEditFaturaField(card, "fatura_no")?.value),
                    satin_alma_tarihi: emptyToNull(findEditFaturaField(card, "satin_alma_tarih")?.value),
					...amortismanSelection,
                    birim_fiyat:       roundFaturaAmount(birimFiyat),
                    birim_miktar:      birimMiktar,
                    para_birimi:       normalizeFaturaCurrency(findEditFaturaField(card, "para_birimi")?.value),
                    kdv_orani:         roundFaturaAmount(kdvOran),
                    kdv_tutar:         roundFaturaAmount(totals.kdvTutar),
                    satin_alma_degeri: roundFaturaAmount(totals.satinAlmaDegeri),
                    fatura_pdf_path:   emptyToNull(inst.currentFilePath),
                    fatura_aciklama:   emptyToNull(findEditFaturaField(card, "fatura_aciklama")?.value),
                };
                if (inst.faturaDbId === null) {
                    yeniFaturalar.push(faturaDatum);
                } else {
					guncelFaturalar.push({ fatura_id: inst.faturaDbId, ...faturaDatum });
				}
            });

            const payload = {
                departman_id:           emptyToNull(v("#edit_departman")),
                departman_urun_grubu_id: emptyToNull(v("#edit_urun_grubu")),
                urun_grubu_urun_alt_grubu_id: emptyToNull(v("#edit_urun_alt_grubu")),
                urun_alt_grubu_urun_cinsi_id: emptyToNull(v("#edit_urun_cinsi")),
                urun_cinsi_marka_id:    emptyToNull(v("#edit_marka")),
                marka_model_id:         emptyToNull(v("#edit_model")),
                urun_durumu_id:         emptyToNull(v("#edit_urun_durumu")),
                zimmet_durumu_id:       emptyToNull(v("#edit_zimmet_durumu")),
                zimmetlenen_tc:         emptyToNull(v("#edit_zimmetlenen_kisi")),
                zimmet_tarihi:          emptyToNull(v("#edit_zimmet_tarihi")),
                lokasyon_id:            emptyToNull(v("#edit_lokasyon")),
                oda_tanim_id:           emptyToNull(v("#edit_oda")),
                yer_tanim_id:           emptyToNull(v("#edit_yer")),
                seri_numarasi:          emptyToNull(v("#edit_seri_numarasi")),
                aciklama:               emptyToNull(v("#edit_aciklama")),
                urun_gorsel_path:       gorselPath?.value || null,
				yeniFaturalar,
                guncelFaturalar,
            };

            showDialogOverlay(editDialog);
            await new Promise((r) => setTimeout(r, 350));
            try {
                const res = await fetch(`${apiBaseUrl}/demirbas/${editCurrentId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                const data = await parseJsonResponse(res, `/demirbas/${editCurrentId}`);
                if (!data.ok) throw new Error(data.error || "Güncelleme başarısız");

                showToast("Demirbaş başarıyla güncellendi.", "success", false);
                editDialog.close();
                const refreshUrl = uiState.demirbas.lastSearchUrl ?? `${apiBaseUrl}/demirbas`;
                renderDemirbasGrid({ url: refreshUrl, paginationLimit: uiState.demirbas.paginationLimit });
            } catch (err) {
                console.error("❌ Edit submit hatası:", err);
                showToast(err.message || "Kayıt sırasında hata oluştu.", "error");
            } finally {
                hideDialogOverlay();
            }
        });
    }
}

// ─── window.openEditDialog — her açılışta çağrılır ────────────────────────────
window.openEditDialog = async function (demirbasId) {
    const editDialog = document.getElementById("editDialog");
    if (!editDialog) return;
    const loadVersion = ++editDialogLoadVersion;

    dbg(`open — demirbasId: ${demirbasId}, instances before reset: ${editFaturaInstances.size}`);
    editCurrentId = demirbasId;
	editAmortismanDefinitions = [];
    ensureDraftUploadNotes(editDialog);

    // Önceki state'i temizle
    resetEditFaturaSection();
    editDialog.removeAttribute("data-submitted");

    // Görsel pond'u temizle
    const gorselInput = resetEditGorselPond(editDialog) || editDialog.querySelector(".edit-gorsel");

    editDialog.showModal();
    editDialog.scrollTop = 0;
    clearEditDialogExpiryTimer();
    resetEditDraftTimerUi(editDialog);

    showDialogOverlay(editDialog, "Yükleniyor...");
    await new Promise((r) => setTimeout(r, 350));
    if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;

    // Veriyi çek + dropdown cache'i al (paralel)
    let row;
    try {
        row = await fetchJson(`/demirbas/${demirbasId}`);
    } catch (err) {
        if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;
        console.error("❌ Edit dialog yükleme hatası:", err);
        hideDialogOverlay();
        editDialog.close();
        showToast("Demirbaş yüklenemedi.", "error");
        return;
    }
    if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;

    // ─── Dropdown'ları doldur & pre-select ──────────────────────────────────
    const cache = await initCreateDialogDropdowns(editDialog, {
        productTreeSelection: {
            departman_id: row.departman_id,
            departman_urun_grubu_id: row.departman_urun_grubu_id,
            urun_grubu_urun_alt_grubu_id: row.urun_grubu_urun_alt_grubu_id,
            urun_alt_grubu_urun_cinsi_id: row.urun_alt_grubu_urun_cinsi_id,
            urun_cinsi_marka_id: row.urun_cinsi_marka_id,
            marka_model_id: row.marka_model_id,
        },
    });
    if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;
    if (cache) {
		editAmortismanDefinitions = cache.amortismanTanimlari || [];
        fillSelect(editDialog.querySelector("#edit_urun_durumu"),      "Ürün durumu seçiniz",  cache.urunDurumlari);
        fillSelect(editDialog.querySelector("#edit_zimmet_durumu"),    "Zimmet durumu seçiniz", cache.zimmetDurumlari);
        fillSelect(editDialog.querySelector("#edit_lokasyon"),         "Lokasyon seçiniz",     cache.lokasyonlar);
        fillSelect(editDialog.querySelector("#edit_oda"),              "Oda seçiniz",          cache.odalar);
        fillSelect(editDialog.querySelector("#edit_yer"),              "Yer seçiniz",          cache.yerler);
    }
    // Kişi dropdown'ını doldur (createDropdowns cache'inden direkt gelmiyor, ayrı)
    try {
        const kisilerData = await fetchJson("/kisiler?durum=1");
        if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;
        const kisilerByTc = new Map();
        const kisiSel = editDialog.querySelector("#edit_zimmetlenen_kisi");
        if (kisiSel) {
            kisiSel.innerHTML = "";
            const ph = document.createElement("option");
            ph.value = ""; ph.textContent = "Zimmetlenen kişi seçiniz"; ph.title = ph.textContent; ph.selected = true;
            kisiSel.appendChild(ph);
            (kisilerData.items || []).forEach(k => {
                kisilerByTc.set(String(k.tc), k);
                const opt = document.createElement("option");
                opt.value = String(k.tc);
                const fullLabel = `${k.first_name} ${k.last_name}`.trim();
                opt.textContent = truncateLabel(fullLabel, 32);
                opt.title = fullLabel;
                kisiSel.appendChild(opt);
            });

            if (row.zimmetlenen_tc && !kisilerByTc.has(String(row.zimmetlenen_tc))) {
                const passiveOption = document.createElement("option");
                passiveOption.value = String(row.zimmetlenen_tc);
                const fullLabel = `${row.zimmetlenen_kisi || row.zimmetlenen_tc} (Pasif)`;
                passiveOption.textContent = truncateLabel(fullLabel, 32);
                passiveOption.title = fullLabel;
                kisiSel.appendChild(passiveOption);
            }

            // Zimmet info card bağla
            const card = editDialog.querySelector("#editZimmetInfoCard");
            if (card && !kisiSel._editZimmetBound) {
                kisiSel._editZimmetBound = true;
                const t1 = card.querySelector(".text-1");
                const t2 = card.querySelector(".text-2");
                const t3 = card.querySelector(".text-3");
                const render = () => {
                    const k = kisilerByTc.get(kisiSel.value);
                    if (t1) t1.textContent = k?.tc || "—";
                    if (t2) t2.textContent = k?.unvan || "—";
                    if (t3) t3.textContent = k?.departman_adi || "—";
                };
                kisiSel.addEventListener("change", render);
                render();
            }
        }
    } catch { /* kişi yüklenemezse devam */ }

    // ─── Değerleri pre-select et ──────────────────────────────────────────────
    const setVal = (sel, val) => {
        const el = editDialog.querySelector(sel);
        if (el && val != null) el.value = String(val);
    };
    setVal("#edit_urun_durumu",     row.urun_durumu_id);
    setVal("#edit_zimmet_durumu",   row.zimmet_durumu_id);
    setVal("#edit_zimmetlenen_kisi", row.zimmetlenen_tc);
    setVal("#edit_lokasyon",        row.lokasyon_id);
    setVal("#edit_oda",             row.oda_tanim_id);
    setVal("#edit_yer",             row.yer_tanim_id);

    // Zimmet info card'ı güncelle
    editDialog.querySelector("#edit_zimmetlenen_kisi")?.dispatchEvent(new Event("change"));

    // Text alanlar
    const snEl = editDialog.querySelector("#edit_seri_numarasi");
    if (snEl) {
        snEl.value = row.seri_numarasi || "";
        if (!snEl._mask) {
            const mask = IMask(snEl, {
                mask: /^[A-Z0-9-]*$/,
                prepare: (s) => s.toUpperCase().replace(/\s+/g, ""),
            });
            snEl._mask = mask;
            snEl.addEventListener("blur", () => {
                const len = snEl.value.length;
                if (len === 0) { snEl.setCustomValidity(""); return; }
                snEl.setCustomValidity(len < 4 ? "Seri numarası en az 4 karakter olmalı." : "");
            });
        }
    }

    const aciklamaEl = editDialog.querySelector("#edit_aciklama");
    if (aciklamaEl) aciklamaEl.value = row.aciklama || "";

    const zimmetTarihEl = editDialog.querySelector("#edit_zimmet_tarihi");
    if (zimmetTarihEl) {
        zimmetTarihEl.max = getTodayLocalDateInputValue();
        zimmetTarihEl.value = toDateInputValue(row.zimmet_tarihi);
    }

    // ─── Fatura kartlarını oluştur ────────────────────────────────────────────
    const faturaList = editDialog.querySelector("#editFaturaList");
    if (faturaList) {
        faturaList.innerHTML = "";
        const faturalar = row.faturalar || [];
        dbg(`fatura count: ${faturalar.length}, instances before forEach: ${editFaturaInstances.size}`);
        faturalar.forEach((f, idx) => {
            if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;
            const card = document.createElement("div");
            card.className = "fatura-container";
            card.dataset.faturaId = String(idx + 1);
            card.innerHTML = buildEditFaturaHtml();
            faturaList.appendChild(card);
            initOneEditFaturaCard(card, {
                faturaDbId: f.fatura_id,
                prefill: f,
                canMount: () => canMountEditDialogNode(card, editDialog, loadVersion, demirbasId),
            });
        });
        renumberEditFaturas();
    }
    if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;

    // ─── Ürün görseli FilePond ────────────────────────────────────────────────
    if (gorselInput && !gorselInput._editPond && canMountEditDialogNode(gorselInput, editDialog, loadVersion, demirbasId)) {
        const pond = FilePond.create(gorselInput, {
            allowMultiple: false,
            maxFiles: 1,
            maxFileSize: "10MB",
            allowImagePreview: false,
            imagePreviewHeight: 270,
            acceptedFileTypes: ["image/png", "image/jpeg", "image/webp"],
            beforeAddFile: () => {
                const state = getEditGorselState(editDialog);
                if (!state || gorselInput.dataset.prefillingLocal === "true") return true;
                state.pendingReplacement = Boolean(state.existingFilePath && state.fileKind === "existing");
                return true;
            },
            server: {
                load: (source, load, error) => {
                    const loadUrl = resolveEditGorselLoadUrl(source);
                    if (!loadUrl) {
                        error("Yuklenemedi");
                        return;
                    }

                    fetch(loadUrl)
                        .then((response) => {
                            if (!response.ok) throw new Error();
                            return response.blob();
                        })
                        .then((blob) => load(blob))
                        .catch(() => error("Yuklenemedi"));
                },
                remove: (_source, load) => {
                    // Görsel de hemen silme — PATCH'te urun_gorsel_path değişince eski silinir.
                    load();
                },
                revert: (serverId, load, error) => {
                    revertDraftFile(serverId)
                        .then(() => load())
                        .catch(() => error());
                },
                process: {
                    url: `${apiBaseUrl}/upload/urun-gorsel`,
                    method: "POST",
                    onload: (responseText) => {
                        const data = JSON.parse(responseText);
                        const filePath = data.filePath || data.path || "";
                        markEditGorselDraftSelected(editDialog, filePath);
                        return data.serverId || filePath;
                    },
                    onerror: (responseText) => {
                        try { return JSON.parse(responseText).error || "Upload başarısız"; }
                        catch { return "Upload başarısız"; }
                    },
                },
            },
        });

        pond.on("addfile", (err, file) => {
            if (err) return;
            if (file?.origin !== "input") return;
            markEditGorselDraftPending(editDialog);
            editDialog.dispatchEvent(new CustomEvent("draft-upload-change"));
        });
        pond.on("removefile", (err, file) => {
            if (err) return;
            removeEditGorselState(editDialog, file?.origin || "");
            editDialog.dispatchEvent(new CustomEvent("draft-upload-change"));
        });
        pond.on("processfile", (err, file) => {
            if (err) return;
            markEditGorselDraftSelected(editDialog, file?.serverId || "");
            editDialog.dispatchEvent(new CustomEvent("draft-upload-change"));
        });
        pond.on("processfilerevert", () => {
            removeEditGorselState(editDialog, "input");
            editDialog.dispatchEvent(new CustomEvent("draft-upload-change"));
        });

        gorselInput._editPond = pond;

        if (row.demirbas_image_path && !isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) {
            setEditGorselState(editDialog, "existing", row.demirbas_image_path, {
                existingFilePath: row.demirbas_image_path,
            });
            gorselInput.dataset.prefillingLocal = "true";
            pond.addFile(row.demirbas_image_path, { type: "local" }).catch((error) => {
                console.warn("Edit gorsel local yukleme hatasi:", error);
                removeEditGorselState(editDialog, "local");
            }).finally(() => {
                delete gorselInput.dataset.prefillingLocal;
            });
        } else {
            setEditGorselState(editDialog, "none", "", { existingFilePath: "" });
        }
    }
    if (isEditDialogLoadStale(editDialog, loadVersion, demirbasId)) return;

    // Tüm doldurma bitti — overlay kaldır, form aktif
    dbg(`filling complete — instances: ${editFaturaInstances.size}`);
    hideDialogOverlay();
};

function bindEditSerialRepeatUx(editDialog) {
    const serialRadio = editDialog.querySelector("#edit_serial_mode_serial");
    const repeatRadio = editDialog.querySelector("#edit_serial_mode_repeat");
    const serialInput = editDialog.querySelector("#edit_seri_numarasi");
    const countInput = editDialog.querySelector("#edit_seri_tekrar_adedi");
    const rows = [...editDialog.querySelectorAll(".serial-mode-row")];
    if (!serialRadio || !repeatRadio || !serialInput || !countInput || serialRadio.dataset.bound) return;

    serialRadio.dataset.bound = "true";

    const sync = () => {
        const repeatMode = repeatRadio.checked;
        serialInput.disabled = repeatMode;
        countInput.disabled = !repeatMode;
        if (repeatMode) serialInput.value = "";
        if (!repeatMode) countInput.value = "";
        rows.forEach((row) => {
            row.classList.toggle("is-active", row.dataset.mode === (repeatMode ? "repeat" : "serial"));
            row.classList.toggle("is-inactive", row.dataset.mode !== (repeatMode ? "repeat" : "serial"));
        });
    };

    serialRadio.addEventListener("change", sync);
    repeatRadio.addEventListener("change", sync);
    countInput.addEventListener("input", () => {
        countInput.value = countInput.value.replace(/\D+/g, "");
    });
    rows.forEach((row) => {
        if (row.dataset.bound) return;
        row.dataset.bound = "true";
        row.addEventListener("click", () => {
            const isRepeat = row.dataset.mode === "repeat";
            if (isRepeat) repeatRadio.checked = true;
            else serialRadio.checked = true;
            sync();
        });
    });

    sync();
}

function resetEditSerialRepeatUx(editDialog) {
    const serialRadio = editDialog.querySelector("#edit_serial_mode_serial");
    const repeatRadio = editDialog.querySelector("#edit_serial_mode_repeat");
    const serialInput = editDialog.querySelector("#edit_seri_numarasi");
    const countInput = editDialog.querySelector("#edit_seri_tekrar_adedi");
    if (serialRadio) serialRadio.checked = true;
    if (repeatRadio) repeatRadio.checked = false;
    if (serialInput) serialInput.disabled = false;
    if (countInput) {
        countInput.value = "";
        countInput.disabled = true;
    }
}

function buildSerialModeCardHtml(prefix) {
    return `
        <div class="serial-mode-card">
            <div class="serial-mode-info">
                <span class="material-icons">info</span>
                <span>Demirbaş formunun tekli mi çoklu mu giriş modunda olacağını seçiniz.</span>
            </div>
            <div class="serial-mode-row is-active" data-mode="serial">
                <label class="serial-mode-radio" for="${prefix}_serial_mode_serial">
                    <input id="${prefix}_serial_mode_serial" type="radio" name="${prefix}_serial_mode" checked />
                    <span>Tekli Giriş</span>
                </label>
                <div class="serial-mode-help">Tekli ise seri numarası giriniz.</div>
                <input id="${prefix}_seri_numarasi" class="form-input serial-mode-input" type="text" placeholder="Seri numarası" maxlength="150"/>
            </div>
            <div class="serial-mode-row is-inactive" data-mode="repeat">
                <label class="serial-mode-radio" for="${prefix}_serial_mode_repeat">
                    <input id="${prefix}_serial_mode_repeat" type="radio" name="${prefix}_serial_mode" />
                    <span>Çoklu Giriş</span>
                </label>
                <div class="serial-mode-help">Çoklu ise tekrar adedini giriniz.</div>
                <input id="${prefix}_seri_tekrar_adedi" class="form-input serial-mode-input serial-mode-count" type="text" inputmode="numeric" placeholder="Adet" disabled />
            </div>
        </div>`;
}

function ensureDraftUploadNotes(dialog) {
    dialog.querySelectorAll(".column-container.create-gorsel").forEach((field) => {
        if (field.querySelector(".draft-upload-note")) return;
        const container = field.querySelector(".gorsel-container");
        container?.insertAdjacentHTML(
            "afterend",
            `<div class="draft-upload-note">
                <span class="material-icons">info</span>
                <span>Yüklenen dosyalar önce taslak olarak tutulur. Kaydetmezseniz 15 dakika sonra otomatik silinir.</span>
            </div>`,
        );
    });
}

function normalizeEditGorselPath(filePath) {
    return typeof filePath === "string" ? filePath.trim() : "";
}

function createEmptyEditGorselState() {
    return {
        hasFile: false,
        fileKind: "none",
        currentFilePath: "",
        existingFilePath: "",
        pendingReplacement: false,
    };
}

function recreateEditGorselInput(editDialog) {
    const currentInput = editDialog.querySelector(".edit-gorsel");
    if (!currentInput) return null;

    const freshInput = currentInput.cloneNode(false);
    freshInput.value = "";
    freshInput._editPond = null;
    freshInput._editImageState = createEmptyEditGorselState();
    delete freshInput.dataset.prefillingLocal;

    currentInput.replaceWith(freshInput);
    return freshInput;
}

function resetEditGorselPond(editDialog, { revertDraft = true } = {}) {
    const gorselInput = editDialog.querySelector(".edit-gorsel");
    const gorselPath = editDialog.querySelector("#edit_urun_gorsel_path");
    const currentPath = gorselPath?.value || "";

    if (revertDraft && isDraftUploadPath(currentPath)) revertDraftFile(currentPath);

    if (gorselInput?._editPond) {
        gorselInput._editPond.destroy();
        gorselInput._editPond = null;
    }
    if (gorselPath) gorselPath.value = "";

    return recreateEditGorselInput(editDialog);
}

function getEditGorselInput(editDialog) {
    return editDialog.querySelector(".edit-gorsel");
}

function getEditGorselHidden(editDialog) {
    return editDialog.querySelector("#edit_urun_gorsel_path");
}

function getEditGorselState(editDialog) {
    const input = getEditGorselInput(editDialog);
    if (!input) return null;
    if (!input._editImageState) {
        input._editImageState = createEmptyEditGorselState();
    }
    return input._editImageState;
}

function setEditGorselState(editDialog, kind, imagePath, { existingFilePath } = {}) {
    const normalizedPath = normalizeEditGorselPath(imagePath);
    const state = getEditGorselState(editDialog);
    const hidden = getEditGorselHidden(editDialog);
    if (!state) return;
    state.fileKind = kind;
    state.currentFilePath = normalizedPath;
    state.hasFile = kind !== "none";
    if (existingFilePath !== undefined) {
        state.existingFilePath = normalizeEditGorselPath(existingFilePath);
    }
    if (kind === "existing" || kind === "none") {
        state.pendingReplacement = false;
    }
    if (hidden) hidden.value = normalizedPath;
}

function resolveEditGorselLoadUrl(filePath) {
    const normalizedPath = normalizeEditGorselPath(filePath);
    if (!normalizedPath) return "";
    if (/^https?:\/\//i.test(normalizedPath) || normalizedPath.startsWith("//")) return normalizedPath;
    if (normalizedPath.startsWith("/uploads/")) return `${apiBaseUrl}${normalizedPath}`;
    return normalizedPath;
}

function markEditGorselDraftPending(editDialog) {
    const state = getEditGorselState(editDialog);
    const hidden = getEditGorselHidden(editDialog);
    if (!state) return;
    state.hasFile = true;
    state.fileKind = "none";
    state.currentFilePath = "";
    if (hidden) hidden.value = "";
}

function markEditGorselDraftSelected(editDialog, imagePath) {
    setEditGorselState(editDialog, "draft", imagePath);
}

function removeEditGorselState(editDialog, fileOrigin) {
    const state = getEditGorselState(editDialog);
    const hidden = getEditGorselHidden(editDialog);
    if (!state) return;

    if (fileOrigin === "input") {
        if (state.existingFilePath) {
            setEditGorselState(editDialog, "existing", state.existingFilePath);
            return;
        }
        setEditGorselState(editDialog, "none", "");
        return;
    }

    if (fileOrigin === "local" && state.pendingReplacement) {
        if (hidden) hidden.value = "";
        return;
    }

    setEditGorselState(editDialog, "none", "", { existingFilePath: "" });
    if (hidden) hidden.value = "";
}

function normalizeEditDialogVisualParity(editDialog) {
    const serialField = editDialog.querySelector(".column-container.field.seri-numarasi");
    if (serialField) {
        const oldInfo = serialField.querySelector(".info");
        const oldInfoHtml = oldInfo?.outerHTML
            || `<span class="info" data-focus="(4-150 karakter, A-Z, 0-9, -)" data-invalid="Seri numarası 4-150 karakter olmalı. Sadece A-Z, 0-9 ve '-' kullanın."></span>`;
        serialField.innerHTML = `<label>Giriş Modu:</label>${buildSerialModeCardHtml("edit")}${oldInfoHtml}`;
    }

    editDialog.querySelector(".column-container.serial-repeat-spacer")?.remove();
    const pillText = editDialog.querySelector(".draft-timer-text");
    if (pillText) pillText.textContent = "15:00";
    ensureDraftUploadNotes(editDialog);
}

function applyEditDialogVisualParity(editDialog) {
    const serialField = editDialog.querySelector(".column-container.field.seri-numarasi");
    if (serialField && !serialField.querySelector(".serial-mode-card")) {
        const oldInfo = serialField.querySelector(".info");
        const oldInfoHtml = oldInfo?.outerHTML
            || `<span class="info" data-focus="(4-150 karakter, A-Z, 0-9, -)" data-invalid="Seri numarası 4-150 karakter olmalı. Sadece A-Z, 0-9 ve '-' kullanın."></span>`;
        serialField.innerHTML = `
            <label>Seri Girişi:</label>
            <div class="serial-mode-card">
                <div class="serial-mode-row">
                    <label class="serial-mode-radio" for="edit_serial_mode_serial">
                        <input id="edit_serial_mode_serial" type="radio" name="edit_serial_mode" checked />
                        <span>Seri Numarası</span>
                    </label>
                    <input id="edit_seri_numarasi" class="form-input" type="text" placeholder="Seri numarası" maxlength="150"/>
                </div>
                <div class="serial-mode-row">
                    <label class="serial-mode-radio" for="edit_serial_mode_repeat">
                        <input id="edit_serial_mode_repeat" type="radio" name="edit_serial_mode" />
                        <span>Tekrar Adedi</span>
                    </label>
                    <input id="edit_seri_tekrar_adedi" class="form-input serial-mode-count" type="text" inputmode="numeric" placeholder="Adet" disabled />
                </div>
            </div>
            ${oldInfoHtml}`;
    }
    editDialog.querySelector(".column-container.serial-repeat-spacer")?.remove();

    if (!editDialog.querySelector(".draft-timer-pill")) {
        const header = editDialog.querySelector(".create-dialog-header");
        header?.insertAdjacentHTML(
            "afterend",
            `
            <div class="draft-timer-pill" hidden>
                <span class="material-icons">schedule</span>
                <span class="draft-timer-text">Taslak dosyalar 15 dk tutulur.</span>
            </div>`,
        );
    }
}

function ensureEditDialogStaticParity(editDialog) {
    const serialField = editDialog.querySelector(".column-container.field.seri-numarasi");
    if (serialField && !editDialog.querySelector("#edit_seri_numarasi_yok")) {
        serialField.insertAdjacentHTML(
            "afterend",
            `
            <div class="column-container serial-repeat-spacer">
                <span></span>
                <div class="serial-repeat-ux">
                    <label class="serial-repeat-toggle">
                        <input id="edit_seri_numarasi_yok" type="checkbox" />
                        <span>Seri numarası yok</span>
                    </label>
                    <input id="edit_seri_tekrar_adedi" class="form-input serial-repeat-count" type="text" inputmode="numeric" placeholder="Tekrar adedi" disabled />
                </div>
            </div>`,
        );
    }

    const zimmetCard = null;
    if (zimmetCard && !zimmetCard.classList.contains("info-card-yeni")) {
        zimmetCard.outerHTML = `
        <div class="info-card-yeni" id="editZimmetInfoCard">
            <div class="info-card-yeni-satir">
                <div class="info-card-yeni-etiket">T.C. Kimlik No</div>
                <div class="info-card-yeni-deger info-card-yeni-deger--mono text-1 zimmet-tc-deger">—</div>
            </div>
            <div class="info-card-yeni-satir">
                <div class="info-card-yeni-etiket">Ünvan</div>
                <div class="info-card-yeni-deger text-2 zimmet-unvan-deger">—</div>
            </div>
            <div class="info-card-yeni-satir">
                <div class="info-card-yeni-etiket">Departman</div>
                <div class="info-card-yeni-deger text-3 zimmet-departman-deger">—</div>
            </div>
        </div>`;
    }
}

function buildEditFaturaHtml() {
    return `
    <div class="fatura-container-header">
      <h5>Fatura</h5>
      <h5 class="fatura-index">#1</h5>
      <button class="delete-button" type="button" aria-label="Faturayi sil">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
      </button>
    </div>
    <div class="fatura-container-content">
      <div class="column-container field">
        <label>Fatura Numarasi:</label>
        <input required data-edit-fatura-field="fatura_no" class="form-input" type="text" placeholder="Fatura no" maxlength="150"/>
        <span class="info" data-invalid="Gecerli bir fatura numarasi giriniz."></span>
      </div>
      <div class="column-container field">
        <label>Satin Alma Tarihi:</label>
        <input data-edit-fatura-field="satin_alma_tarih" type="date" class="form-input" required/>
        <span class="info" data-invalid="Gecerli bir satin alma tarihi giriniz."></span>
      </div>
      <div class="column-container field birim-fiyat">
        <label>Birim Fiyat:</label>
        <div class="currency-input-wrap">
          <input data-edit-fatura-field="birim_fiyat" class="form-input currency-input" type="text" placeholder="Birim fiyat" required/>
          <select data-edit-fatura-field="para_birimi" class="currency-select" aria-label="Para birimi">
            ${buildCurrencyOptions("TRY")}
          </select>
        </div>
        <span class="info" data-invalid="Gecerli bir birim fiyat giriniz."></span>
      </div>
	  <div class="column-container field">
		<label>Faydal&#305; &#214;m&#252;r:</label>
		<select data-edit-fatura-field="amortisman" class="form-select"></select>
		<span class="info" data-focus="Faydal&#305; &#246;m&#252;r tan&#305;m&#305; veya Amortisman uygulanmaz se&#231;iniz." data-invalid="Faydal&#305; &#246;m&#252;r se&#231;imi zorunludur."></span>
	  </div>
	  <div class="info-card-yeni fatura-amortisman-preview">
		<div class="info-card-yeni-satir">
		  <div class="info-card-yeni-etiket">Faydal&#305; &#214;m&#252;r</div>
		  <div class="info-card-yeni-deger fatura-amortisman-yil">&#8212;</div>
		</div>
		<div class="info-card-yeni-satir">
		  <div class="info-card-yeni-etiket">Tahmini Biti&#351;</div>
		  <div class="info-card-yeni-deger fatura-amortisman-bitis">&#8212;</div>
		</div>
	  </div>
      <div class="column-container field">
        <label>Temsil Adedi:</label>
        <input data-edit-fatura-field="birim_miktar" class="form-input" type="text" inputmode="numeric" value="1" placeholder="Temsil adedi" required/>
        <span class="info" data-focus="Bu demirbas kaydi faturada kac adet urunu temsil ediyor." data-invalid="Temsil adedi en az 1 olan tam sayi olmalidir."></span>
      </div>
      <div class="column-container field kdv-oran">
        <label>KDV Orani:</label>
        <select data-edit-fatura-field="kdv_oran" class="form-select" required>
          <option value="" selected>KDV orani seciniz</option>
          ${buildFaturaKdvOptions()}
        </select>
        <span class="info" data-focus="KDV orani seciniz." data-invalid="Gecerli bir KDV orani seciniz."></span>
      </div>
      <div class="info-card-yeni info-card-yeni--financial">
        <div class="info-card-yeni-satir">
          <div class="info-card-yeni-etiket">KDV Tutar</div>
          <div class="info-card-yeni-deger info-card-yeni-deger--mono fatura-kdv-deger">—</div>
        </div>
        <div class="info-card-yeni-satir">
          <div class="info-card-yeni-etiket">Satın Alma Değeri</div>
          <div class="info-card-yeni-deger info-card-yeni-deger--mono fatura-toplam-deger">—</div>
        </div>
      </div>
      <div class="column-container create-gorsel create-fatura">
        <label>Fatura PDF</label>
        <div class="gorsel-container">
          <input type="file" class="filepond fatura"/>
          <input type="hidden" class="fatura-pdf-path" value=""/>
        </div>
        <div class="draft-upload-note">
          <span class="material-icons">info</span>
          <span>Yüklenen dosyalar önce taslak olarak tutulur. Kaydetmezseniz 15 dakika sonra otomatik silinir.</span>
        </div>
      </div>
      <div class="column-container field create-fatura-aciklama">
        <label>Fatura Aciklama:</label>
        <textarea data-edit-fatura-field="fatura_aciklama" class="form-textarea" placeholder="Fatura ile ilgili not (opsiyonel)" maxlength="500"></textarea>
        <span class="info" data-focus="Maksimum 500 karakter." data-invalid="Aciklama 500 karakteri gecemez."></span>
      </div>
    </div>`;
}

function initOneEditFaturaCard(cardEl, { faturaDbId = null, prefill = null, canMount = null } = {}) {
    const faturaId = cardEl.dataset.faturaId;
    if (!faturaId || editFaturaInstances.has(faturaId)) return;
    if (typeof canMount === "function" && !canMount()) return;

    const pdfInput = cardEl.querySelector('input[type="file"].filepond.fatura');
    const pdfPathHidden = cardEl.querySelector(".fatura-pdf-path");
    const tarihEl = findEditFaturaField(cardEl, "satin_alma_tarih");
    const fiyatEl = findEditFaturaField(cardEl, "birim_fiyat");
    const quantityEl = findEditFaturaField(cardEl, "birim_miktar");
    const kdvEl = findEditFaturaField(cardEl, "kdv_oran");
    const currencyEl = findEditFaturaField(cardEl, "para_birimi");
	const amortismanEl = findEditFaturaField(cardEl, "amortisman");
    const aciklamaEl = findEditFaturaField(cardEl, "fatura_aciklama");
    const noEl = findEditFaturaField(cardEl, "fatura_no");
	const existingUnclassified = Boolean(faturaDbId)
		&& !prefill?.amortisman_tanim_id
		&& !prefill?.amortisman_uygulanmaz;
	populateAmortismanSelect(amortismanEl, editAmortismanDefinitions, {
		selectedDefinitionId: prefill?.amortisman_tanim_id,
		uygulanmaz: prefill?.amortisman_uygulanmaz,
		allowUnclassified: existingUnclassified,
		currentDefinition: prefill?.amortisman_tanim_id ? {
			id: prefill.amortisman_tanim_id,
			name: prefill.amortisman_tanim_adi,
			faydali_omur_yil: prefill.faydali_omur_yil,
			is_active: Boolean(prefill.amortisman_tanim_aktif),
		} : null,
	});
	if (amortismanEl && faturaDbId) {
		amortismanEl.disabled = true;
		amortismanEl.required = false;
		amortismanEl.title = "Kaydedilmis alim partisinin faydali omur secimi Amortisman Takibi ekranindan degistirilir.";
		const field = amortismanEl.closest(".column-container");
		if (field && !field.querySelector(".amortisman-edit-lock-note")) {
			field.insertAdjacentHTML(
				"beforeend",
				'<span class="amortisman-edit-lock-note">Kaydedilmi\u015f al\u0131m partisinin se\u00e7imi Amortisman Takibi ekran\u0131ndan de\u011fi\u015ftirilebilir.</span>',
			);
		}
	}
	const deleteButton = cardEl.querySelector(".delete-button");
	if (deleteButton && faturaDbId) {
		deleteButton.hidden = true;
		deleteButton.title = "Kaydedilmis alim partileri fiziksel olarak silinemez.";
	}

    const setPdfState = (inst, kind, filePath) => {
        if (!inst) return;
        inst.fileKind = kind;
        inst.currentFilePath = filePath || "";
        inst.hasFile = kind !== "none";
        if (pdfPathHidden) pdfPathHidden.value = filePath || "";
    };

    if (tarihEl) {
        tarihEl.max = getTodayLocalDateInputValue();
        if (!tarihEl._boundFuture) {
            tarihEl._boundFuture = true;
            const validate = () => {
                if (!tarihEl.value) {
                    tarihEl.setCustomValidity("");
                    return;
                }
                const today = getTodayLocalDateInputValue();
                tarihEl.setCustomValidity(tarihEl.value > today ? "Gelecekte bir tarih secemezsiniz." : "");
            };
            tarihEl.addEventListener("change", validate);
			tarihEl.addEventListener("change", () => updateAmortismanPreview(cardEl));
            tarihEl.addEventListener("blur", validate);
        }
    }
	if (amortismanEl && !amortismanEl.dataset.previewBound) {
		amortismanEl.dataset.previewBound = "true";
		amortismanEl.addEventListener("change", () => updateAmortismanPreview(cardEl));
	}

    bindEditQuantityValidation(quantityEl, () => updateEditInfoCard(faturaId));

    let pond = null;
    if (pdfInput && (!canMount || canMount()) && pdfInput.isConnected) {
        pond = FilePond.create(pdfInput, {
            allowMultiple: false,
            maxFiles: 1,
            maxFileSize: "10MB",
            acceptedFileTypes: ["application/pdf"],
            server: {
                load: (source, load, error) => {
                    fetch(source)
                        .then((response) => { if (!response.ok) throw new Error(); return response.blob(); })
                        .then((blob) => load(blob))
                        .catch(() => error("Yuklenemedi"));
                },
                remove: (_source, load) => load(),
                revert: (serverId, load, error) => {
                    revertDraftFile(serverId)
                        .then(() => load())
                        .catch(() => error("Silinemedi"));
                },
                process: {
                    url: `${apiBaseUrl}/upload/fatura`,
                    method: "POST",
                    onload: (response) => {
                        try {
                            const data = JSON.parse(response);
                            const filePath = data.filePath || data.path || "";
                            if (pdfPathHidden) pdfPathHidden.value = filePath;
                            return data.serverId || filePath;
                        } catch {
                            if (pdfPathHidden) pdfPathHidden.value = response;
                            return response;
                        }
                    },
                    onerror: (response) => {
                        try {
                            const data = JSON.parse(response);
                            return data.error || "Upload basarisiz";
                        } catch {
                            return response || "Upload basarisiz";
                        }
                    },
                },
            },
        });

        setEditUploadState(cardEl, "is-invalid");
        pond.on("updatefiles", (files) => setEditUploadState(cardEl, files.length ? "is-valid" : "is-invalid"));
        pond.on("processfile", (err, file) => {
            if (err) return;
            setPdfState(editFaturaInstances.get(faturaId), "draft", file?.serverId || "");
            setEditUploadState(cardEl, "is-valid");
            document.getElementById("editDialog")?.dispatchEvent(new CustomEvent("draft-upload-change"));
        });
        pond.on("removefile", () => {
            setPdfState(editFaturaInstances.get(faturaId), "none", "");
            setEditUploadState(cardEl, "is-invalid");
            document.getElementById("editDialog")?.dispatchEvent(new CustomEvent("draft-upload-change"));
        });
        pond.on("processfilerevert", () => {
            setPdfState(editFaturaInstances.get(faturaId), "none", "");
            setEditUploadState(cardEl, "is-invalid");
            document.getElementById("editDialog")?.dispatchEvent(new CustomEvent("draft-upload-change"));
        });
    }

    let anFiyat = null;
    if (fiyatEl && !fiyatEl._an) {
        anFiyat = new AutoNumeric(fiyatEl, {
            currencySymbol: ` ${getFaturaCurrencySymbol(currencyEl?.value)}`,
            currencySymbolPlacement: "s",
            decimalCharacter: ",",
            digitGroupSeparator: ".",
            decimalPlaces: 2,
            minimumValue: "0",
            maximumValue: "999999999",
            modifyValueOnWheel: false,
            emptyInputBehavior: "null",
        });
        fiyatEl._an = anFiyat;
        bindAmountPasteGuard(fiyatEl);
        fiyatEl.addEventListener("blur", () => {
            if (anFiyat.getNumericString() === null) fiyatEl.value = "";
        });
        fiyatEl.addEventListener("input", () => updateEditInfoCard(faturaId));
    } else if (fiyatEl?._an) {
        anFiyat = fiyatEl._an;
    }
    bindAmountPasteGuard(fiyatEl);

    if (kdvEl) kdvEl.addEventListener("change", () => updateEditInfoCard(faturaId));
    if (currencyEl && !currencyEl.dataset.bound) {
        currencyEl.dataset.bound = "true";
        currencyEl.addEventListener("change", () => {
            anFiyat?.update({ currencySymbol: ` ${getFaturaCurrencySymbol(currencyEl.value)}` });
            updateEditInfoCard(faturaId);
        });
    }

    editFaturaInstances.set(faturaId, {
        root: cardEl,
        pond,
        anFiyat,
        quantityEl,
        kdvEl,
        currencyEl,
        faturaDbId,
        hasFile: false,
        fileKind: "none",
        currentFilePath: "",
    });

    const inst = editFaturaInstances.get(faturaId);

    if (prefill) {
        if (noEl && prefill.fatura_numarasi) noEl.value = prefill.fatura_numarasi;
        if (tarihEl) tarihEl.value = toDateInputValue(prefill.satin_alma_tarihi);
        if (currencyEl) currencyEl.value = normalizeFaturaCurrency(prefill.para_birimi);
        if (anFiyat) {
            anFiyat.update({ currencySymbol: ` ${getFaturaCurrencySymbol(currencyEl?.value)}` });
            if (prefill.birim_fiyat != null) anFiyat.set(prefill.birim_fiyat);
        }
        if (quantityEl) quantityEl.value = normalizeFaturaQuantityInputValue(prefill.birim_miktar, 1);
        if (kdvEl && prefill.kdv_orani != null) kdvEl.value = String(Number(prefill.kdv_orani));
        if (aciklamaEl && prefill.fatura_aciklama) aciklamaEl.value = prefill.fatura_aciklama;

        if (pond && prefill.fatura_pdf_path && (!canMount || canMount()) && cardEl.isConnected) {
            pond.addFile(prefill.fatura_pdf_path, { type: "local" });
            setPdfState(inst, "existing", prefill.fatura_pdf_path);
            setEditUploadState(cardEl, "is-valid");
        }
    }

    updateEditInfoCard(faturaId);
	updateAmortismanPreview(cardEl);
}

function destroyOneEditFaturaCard(cardEl) {
    const faturaId = cardEl.dataset.faturaId;
    if (!faturaId) return;

    const inst = editFaturaInstances.get(faturaId);
    const hidden = inst?.root?.querySelector(".fatura-pdf-path");
    const currentPath = inst?.currentFilePath || hidden?.value || "";

    if (inst?.pond) inst.pond.destroy();
    if (inst?.anFiyat) {
        try { inst.anFiyat.remove(); } catch { /* ignore */ }
    }
    if (hidden) hidden.value = "";
    if (isDraftUploadPath(currentPath)) revertDraftFile(currentPath);
	editFaturaInstances.delete(faturaId);
    document.getElementById("editDialog")?.dispatchEvent(new CustomEvent("draft-upload-change"));
}

function resetEditFaturaSection() {
    editFaturaInstances.forEach((inst) => {
        const currentPath = inst?.currentFilePath || inst?.root?.querySelector(".fatura-pdf-path")?.value || "";
        if (isDraftUploadPath(currentPath)) revertDraftFile(currentPath);
        if (inst?.pond) inst.pond.destroy();
        if (inst?.anFiyat) try { inst.anFiyat.remove(); } catch { /* ignore */ }
    });
    editFaturaInstances.clear();
    const list = document.querySelector("#editFaturaList");
    if (list) list.innerHTML = "";
}
